import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useBrand } from './BrandProvider';
import {
  DARK_QUERY,
  DEFAULT_THEME_PREFERENCE,
  THEME_STORAGE_KEY,
  channelsToCssColor,
  parseThemePreference,
  readThemePreference,
  resolveTheme,
  writeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from './themeModel';

/**
 * Appearance (M6, T-53, Q-015; DESIGN_SYSTEM.md §2.7): "מערכת · בהיר · כהה" in
 * the avatar menu. Light is the default for this release; the choice is
 * remembered per browser and also applies to the login page and the public
 * portal (same browser, same preference). The no-flash script in index.html
 * sets `data-theme` before the first paint with the same rules (themeModel.ts);
 * this provider keeps it, `color-scheme` (tokens.css) and `<meta name="theme-color">`
 * in step, follows `prefers-color-scheme` live for "system", and syncs tabs.
 * Tokens do the rest: components need no `dark:` classes.
 */

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const FALLBACK: ThemeContextValue = {
  preference: DEFAULT_THEME_PREFERENCE,
  resolved: 'light',
  setPreference: () => {},
};

const ThemeContext = createContext<ThemeContextValue>(FALLBACK);

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readThemePreference(safeStorage()));
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const resolved = resolveTheme(preference, systemDark);

  // "system" follows the OS live.
  useEffect(() => {
    let mql: MediaQueryList | null = null;
    try {
      mql = window.matchMedia?.(DARK_QUERY) ?? null;
    } catch {
      mql = null;
    }
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql?.removeEventListener?.('change', onChange);
  }, []);

  // Another tab changed the preference.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY || e.key === null) setPreferenceState(parseThemePreference(e.newValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Before paint: the attribute, then the browser chrome colour (the canvas in dark).
  useLayoutEffect(() => {
    const root = document.documentElement;
    const changed = root.getAttribute('data-theme') !== resolved;
    if (changed) {
      // No colour transitions while the whole page swaps tokens (index.css dark block).
      root.setAttribute('data-theme-switching', '');
      root.setAttribute('data-theme', resolved);
    }
    const meta = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
    if (meta) {
      const dark = resolved === 'dark'
        ? channelsToCssColor(getComputedStyle(root).getPropertyValue('--c-canvas'))
        : null;
      meta.content = dark ?? brand.themeColor;
    }
    if (!changed) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => root.removeAttribute('data-theme-switching'));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      root.removeAttribute('data-theme-switching');
    };
  }, [resolved, brand.themeColor]);

  const setPreference = useCallback((next: ThemePreference) => {
    writeThemePreference(safeStorage(), next);
    setPreferenceState(next);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** The appearance preference and the theme in effect (light outside a provider). */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
